import { useAppAccessContext } from 'contexts/AppAccessContext';
import { ALL_APPS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { RenderPartV2CellProps } from 'ui-component/DataGrid/Render/RenderPartV2Cell';
import { useSelector } from 'store';
import {
  Box,
  Chip,
  GridLegacy,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CopyClipboard from 'ui-component/extended/CopyClipboard';
import GenericPartAvatar from 'ui-component/extended/Avatars/GenericPartAvatar';
import _ from 'lodash';
import reactStringReplace from 'react-string-replace';
import { useTheme } from '@mui/material/styles';
import { removeNonAlphanumericOrPeriod } from 'utils/functions';
import { useMemo, useState, useCallback } from 'react';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags, TempCompactPartCellType } from 'types';

const POPOVER_MIN_WIDTH = 300;
const POPOVER_MAX_WIDTH = 500;

const PartV2CellCompact = ({
  orgPart,
  publicPart,
  noPart,
  altCount,
  stockLot,
  searchParams,
  unapprovedAltCount,
}: RenderPartV2CellProps) => {
  const { isPermissionDisabled } = useAppAccessContext();
  const customPnsEnabled = !isPermissionDisabled({
    app: ALL_APPS.PART_LIBRARY.id,
    feature: ALL_FEATURE_IDS.CUSTOM_PNS,
  });
  const { activeOrg } = useSelector((state) => state.org);
  const theme = useTheme();
  const compactPartCellType =
    (useFeature(FeatureFlags.tempCompactPartCell)
      .value as TempCompactPartCellType | null) ??
    TempCompactPartCellType.DEFAULT;

  const partCPID = orgPart?.cofactrId ?? publicPart?.cofactrId;
  const mpn = orgPart?.mpn ?? publicPart?.mpn;
  const description = orgPart?.description ?? publicPart?.description;
  const aliasMpns = orgPart?.aliasMpns ?? publicPart?.aliasMpns ?? [];
  const mfgAliases = orgPart?.aliasMfgs ?? publicPart?.aliasMfgs ?? [];
  const stockLotId = typeof stockLot === 'string' ? stockLot : stockLot?.id;
  const partId = orgPart?.id ?? publicPart?.id;
  const partHeroImage = orgPart?.heroImage ?? publicPart?.heroImage;
  const hasManufacturer =
    !!noPart || !!orgPart?.manufacturer || !!publicPart?.manufacturer;
  const manufacturer = orgPart?.manufacturer ?? publicPart?.manufacturer ?? '-';

  const splitSearchString = useMemo(
    () => _.chain(searchParams).split(' ').compact().value(),
    [searchParams]
  );

  const searchStringAltMatches = _.filter(aliasMpns, (a) =>
    _.some(splitSearchString, (s) =>
      _.includes(
        removeNonAlphanumericOrPeriod(a)?.toLowerCase(),
        removeNonAlphanumericOrPeriod(s)?.toLowerCase()
      )
    )
  );

  const searchHighlightString = (input: string) => {
    if (!searchParams) return input;
    const escapedSplitSearchString = _.map(splitSearchString, (s) =>
      _.replace(s, /[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const highlightRegex = _.join(escapedSplitSearchString, '|');
    return reactStringReplace(
      input,
      new RegExp(`(${highlightRegex})`, 'i'),
      (match, i) => (
        <span
          key={i}
          style={{ color: theme.palette.success.main, fontWeight: 'bold' }}
        >
          {match}
        </span>
      )
    );
  };

  const isDefaultMode = compactPartCellType === TempCompactPartCellType.DEFAULT;
  const isImageWithMpnMode =
    compactPartCellType === TempCompactPartCellType.IMAGE_WITH_MPN;

  const shouldShowImage = isDefaultMode || isImageWithMpnMode;
  const shouldShowManufacturer = isDefaultMode;
  const shouldShowMpn =
    isDefaultMode ||
    isImageWithMpnMode ||
    compactPartCellType === TempCompactPartCellType.MPN_ONLY;
  const shouldShowCustomId = isDefaultMode;
  const shouldShowDescription = isDefaultMode;
  const shouldShowAltMatches = isDefaultMode;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          py: isDefaultMode ? 0 : '4px',
        }}
      >
        <GridLegacy container columnSpacing={1.5} alignItems="center">
          {shouldShowImage && (
            <GridLegacy item xs="auto">
              <GenericPartAvatar
                partId={partId}
                partHeroImage={partHeroImage}
                partCPID={partCPID}
                altCount={altCount}
                stockLotId={stockLotId}
                searchParams={searchParams}
                unapprovedAltCount={unapprovedAltCount}
                partType={orgPart?.partType}
                compact
              />
            </GridLegacy>
          )}
          <GridLegacy
            item
            xs
            sx={{
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              ...(isDefaultMode ? { gap: 0 } : {}),
            }}
          >
            {shouldShowManufacturer && hasManufacturer && (
              <Typography
                variant={isDefaultMode ? 'caption' : 'body2'}
                sx={
                  isDefaultMode
                    ? {
                        lineHeight: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '20px',
                      }
                    : undefined
                }
              >
                {noPart
                  ? 'Unable to find match for:'
                  : searchHighlightString(manufacturer)}
              </Typography>
            )}
            {shouldShowMpn && !!mpn && mpn !== orgPart?.customId && (
              <Typography
                variant="body2"
                sx={
                  isDefaultMode
                    ? {
                        lineHeight: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        maxHeight: '13px',
                      }
                    : undefined
                }
              >
                {searchHighlightString(mpn ?? noPart ?? '-')}
                {mpn && (
                  <CopyClipboard label="Part MPN" input={noPart || mpn} />
                )}
              </Typography>
            )}
            {shouldShowCustomId && customPnsEnabled && orgPart?.customId && (
              <Typography variant="body2">
                {searchHighlightString(orgPart.customId)}
                <CopyClipboard
                  label={`${activeOrg?.name || 'Custom'} Part Number`}
                  input={orgPart.customId}
                />
                {orgPart?.revision && (
                  <Chip
                    size="small"
                    label={`Rev ${searchHighlightString(orgPart.revision)}`}
                  />
                )}
              </Typography>
            )}
            {shouldShowDescription && !!description && (
              <Typography
                noWrap
                variant={isDefaultMode ? 'caption' : 'body2'}
                sx={
                  isDefaultMode
                    ? {
                        lineHeight: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '20px',
                      }
                    : undefined
                }
              >
                {searchHighlightString(description)}
              </Typography>
            )}
            {shouldShowAltMatches &&
              searchStringAltMatches.length > 0 &&
              !_.includes(
                orgPart?.mpn?.toLowerCase(),
                searchParams?.toLowerCase()
              ) && (
                <Typography sx={{ fontStyle: 'italic' }} variant="caption">
                  This part is also known as:{' '}
                  {searchHighlightString(searchStringAltMatches.join(', '))}
                </Typography>
              )}
          </GridLegacy>
        </GridLegacy>
      </Box>
      {!isDefaultMode && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleMouseLeave}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          disableRestoreFocus
          sx={{
            pointerEvents: 'none',
          }}
          PaperProps={{
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            sx: {
              pointerEvents: 'auto',
              p: 2,
              minWidth: POPOVER_MIN_WIDTH,
              maxWidth: POPOVER_MAX_WIDTH,
              mt: -0.5,
            },
          }}
        >
          <Stack spacing={1.5}>
            <GridLegacy container columnSpacing={1.5} alignItems="center">
              <GridLegacy item xs="auto">
                <GenericPartAvatar
                  partId={partId}
                  partHeroImage={partHeroImage}
                  partCPID={partCPID}
                  altCount={altCount}
                  stockLotId={stockLotId}
                  searchParams={searchParams}
                  unapprovedAltCount={unapprovedAltCount}
                  partType={orgPart?.partType}
                />
              </GridLegacy>
              <GridLegacy item xs>
                {hasManufacturer && (
                  <Tooltip
                    title={
                      mfgAliases.length > 0
                        ? `This manufacturer is also known as: ${mfgAliases.join(
                            ', '
                          )}`
                        : undefined
                    }
                  >
                    <Typography
                      variant="body1"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {noPart
                        ? 'Unable to find match for:'
                        : searchHighlightString(manufacturer)}
                    </Typography>
                  </Tooltip>
                )}
                {!!mpn && mpn !== orgPart?.customId && (
                  <Tooltip
                    title={
                      (orgPart?.aliasMpns || []).length > 0
                        ? `This part is also known as: ${orgPart?.aliasMpns.join(
                            ', '
                          )}`
                        : undefined
                    }
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {searchHighlightString(mpn ?? noPart ?? '-')}
                      {mpn && (
                        <CopyClipboard label="Part MPN" input={noPart || mpn} />
                      )}
                    </Typography>
                  </Tooltip>
                )}
                {customPnsEnabled && orgPart?.customId && (
                  <Typography
                    variant="subtitle1"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {searchHighlightString(orgPart.customId)}
                    <CopyClipboard
                      label={`${activeOrg?.name || 'Custom'} Part Number`}
                      input={orgPart.customId}
                    />
                    {orgPart?.revision && (
                      <Chip
                        size="small"
                        label={`Rev ${searchHighlightString(orgPart.revision)}`}
                      />
                    )}
                  </Typography>
                )}
                {!!description && (
                  <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {searchHighlightString(description)}
                  </Typography>
                )}
                {searchStringAltMatches.length > 0 &&
                  !_.includes(
                    orgPart?.mpn?.toLowerCase(),
                    searchParams?.toLowerCase()
                  ) && (
                    <Typography
                      sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}
                      variant="caption"
                    >
                      This part is also known as:{' '}
                      {searchHighlightString(searchStringAltMatches.join(', '))}
                    </Typography>
                  )}
              </GridLegacy>
            </GridLegacy>
          </Stack>
        </Popover>
      )}
    </>
  );
};

export default PartV2CellCompact;
